/**
 * Settings -> Integrations: the blocklist the site scanner asks about a prospect.
 *
 * One key, and the panel exists mostly to make three things sayable that were
 * not sayable anywhere before it:
 *
 *  - whether the lookup is switched on at all. Without a key the scanner does
 *    not report "unknown", it simply does not ask, and no screen said which of
 *    those two was happening.
 *  - what the check costs. It is a paid API, so the number of stored answers is
 *    on screen next to the key rather than in a bill a month later.
 *  - that an empty box means OFF. Clearing the key stops the lookup, and a
 *    field that silently keeps working after being emptied is the worst kind.
 *
 * ⚠ NO CONNECTION TEST BUTTON, on purpose. A test means spending a real lookup
 * on somebody's domain, and the only honest test is the next scan of a real
 * prospect. So the panel reports what the cache already knows instead.
 */
import { useEffect, useState } from "react";
import { ShieldAlert, Save } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { getWebRiskSettings, saveWebRiskSettings, type WebRiskSettings } from "../../../../lib/webRisk";
import { Btn, StatusPill, fmtDateTime } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, Note, SaveRow, SecretPill } from "../panel/fields";

export function WebRiskCard() {
  const t = useT();
  const [settings, setSettings] = useState<WebRiskSettings | null>(null);
  const [loading, setLoading] = useState(true);
  // Starts empty and stays empty: the stored key never comes back from the
  // server, so an empty box means "leave it alone" until the operator types.
  const [apiKey, setApiKey] = useState("");
  const [touched, setTouched] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  useEffect(() => {
    getWebRiskSettings()
      .then(setSettings)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PanelLoader />;
  if (!settings) return null;

  // Emptying the box and saving DELETES the key. Every line of copy around the
  // field has to agree about that at the moment it is true, or the hint tells
  // the operator to leave it empty while the warning says empty deletes it.
  const clearing = touched && !apiKey.trim() && settings.keySet;

  const save = async () => {
    setSaveState("saving");
    try {
      await saveWebRiskSettings(apiKey.trim());
      setSettings(await getWebRiskSettings());
      setApiKey("");
      setTouched(false);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  };

  return (
    <IntegrationCard
      icon={ShieldAlert}
      title={t("settings.webRisk.title")}
      desc={t("settings.webRisk.desc", { source: settings.source })}
      status={
        <ConnStatus ok={settings.keySet} okLabel={t("settings.webRisk.on")} offLabel={t("settings.webRisk.off")} />
      }
    >
      <Note tone="info">{t("settings.webRisk.what", { source: settings.source })}</Note>

      <Field
        label={t("settings.webRisk.apiKey")}
        hint={
          clearing
            ? t("settings.webRisk.clearWarning")
            : settings.keySet
              ? t("settings.webRisk.apiKeyStored")
              : t("settings.webRisk.apiKeyMissing", { source: settings.source })
        }
      >
        <SecretPill
          value={apiKey}
          onChange={(v) => {
            setApiKey(v);
            setTouched(true);
            setSaveState("idle");
          }}
          placeholder={
            clearing
              ? t("settings.webRisk.apiKeyClears")
              : settings.keySet
                ? t("settings.webRisk.apiKeyKeep")
                : t("settings.webRisk.apiKeyEmpty")
          }
          reveal
          showLabel={t("settings.webRisk.reveal")}
          hideLabel={t("settings.webRisk.hide")}
        />
      </Field>

      {/* Said once, loudly, and the field's own hint and placeholder say the
          same thing at the same moment rather than the opposite. */}
      {clearing ? <Note tone="warn">{t("settings.webRisk.clearWarning")}</Note> : null}

      <div className="flex flex-wrap items-center gap-2">
        <StatusPill tone="mut">{t("settings.webRisk.cached", { count: settings.cached })}</StatusPill>
        <StatusPill tone={settings.listed > 0 ? "lt" : "mut"}>
          {t("settings.webRisk.listed", { count: settings.listed })}
        </StatusPill>
        {settings.newestAt ? (
          <StatusPill tone="mut">
            {t("settings.webRisk.newest", { when: fmtDateTime(settings.newestAt) })}
          </StatusPill>
        ) : null}
      </div>

      <SaveRow>
        <Btn onClick={save} disabled={!touched || saveState === "saving"}>
          <Save className="size-4" /> {t("common.save")}
        </Btn>
        {saveState === "saved" ? <span className="crm-sub">{t("common.saved")}</span> : null}
        {saveState === "error" ? <span className="crm-sub">{t("common.error")}</span> : null}
      </SaveRow>
    </IntegrationCard>
  );
}
